import { CheckoutProvider, PaymentElement, useCheckout } from '@stripe/react-stripe-js';
import { loadStripe, StripeCheckoutConfirmResult } from '@stripe/stripe-js';
import { css } from 'aphrodite';
import { inject } from 'mobx-react';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { IModalContext, ModalChildComponentContextKey } from '../../../../../models';
import { useErrorMessages } from '../../../../../models/hooks/appStateHooks';
import { useClientStripeSessionByToken } from '../../../../../queries';
import { Button } from '../../../../../web/components/Button';
import { LoadingLogo } from '../../../../../web/components/LoadingLogo';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { TextInput } from '../../../../../web/components/TextInput';
import { bs } from '../../../../../web/styles/styles';
import { IEventSurveyResponse } from '../../../../models/Api';
import { styleSheet } from './styles';

interface IProps extends IModalContext {
	stripePublicId: string;
	surveyResponse: IEventSurveyResponse;
	onRequestClose: () => void;
	token: string;
	ticketsOrder: number;
}

export const CheckoutFormProviderWrap = ({
	stripePublicId,
	surveyResponse,
	onRequestClose,
	parentModal,
	token,
	ticketsOrder,
}: IProps) => {
	const stripeSession = useClientStripeSessionByToken();
	const stripePromise = React.useRef(() => {
		if (stripePublicId) {
			const sp = loadStripe(stripePublicId);
			return sp;
		}
		return null;
	}).current();

	const fetchClientSecret = React.useCallback(async () => {
		const response = await stripeSession.mutateAsync({
			token,
			quantity: ticketsOrder,
		});
		return response; // Assuming server returns { client_secret: '...' }
	}, [stripeSession, token, ticketsOrder]);

	return (
		<CheckoutProvider
			stripe={stripePromise}
			options={{
				fetchClientSecret,
			}}
		>
			<StripeCheckoutForm
				onSuccessClose={onRequestClose}
				onCloseModal={parentModal.onRequestClose}
				attendeeEmail={surveyResponse?.attendee?.email}
			/>
		</CheckoutProvider>
	);
};

export const CheckoutForm = inject(ModalChildComponentContextKey)(CheckoutFormProviderWrap);

export const StripeCheckoutForm = ({
	onSuccessClose,
	onCloseModal,
	attendeeEmail,
}: { onSuccessClose: () => void; onCloseModal: (sessionId: string) => void; attendeeEmail: string }) => {
	const checkout = useCheckout();

	const [isLoading, setIsLoading] = React.useState(false);
	const [isScriptLoading, setIsScriptLoading] = React.useState(true);
	const [email, setEmail] = React.useState<string>(attendeeEmail || '');
	const errors = useErrorMessages();

	const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

	const location = useLocation();

	React.useEffect(() => {
		// If the user navigates away from the page, close the modal
		if (location.search) {
			onSuccessClose();
		}
	}, [location.search, onSuccessClose]);

	React.useEffect(() => {
		if (checkout?.id) {
			setTimeout(() => {
				setIsScriptLoading(false);
			}, 1000);
		}
	}, [checkout?.id]);

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		// We don't want to let default form submission happen here,
		// which would refresh the page.
		event.preventDefault();
		event.stopPropagation(); // Prevent the event from bubbling up
		// Prevent the form from being submitted again while processing
		// the payment
		setIsLoading(true);
		setErrorMessage(null);

		if (!checkout) {
			setErrorMessage('Payment processing failed. Please try again.');
			setIsLoading(false);
			return;
		}
		if (!email?.includes('@')) {
			setErrorMessage('Please enter a valid email address.');
			setIsLoading(false);
		}

		try {
			const result: StripeCheckoutConfirmResult = await checkout.confirm({
				// Use the email entered by the user
				email,
			});

			if (result.type === 'error') {
				setErrorMessage(result.error.message);
				setIsLoading(false);

				return;
			}
			if (result.type === 'success') {
				setIsLoading(false);
				onSuccessClose();
			}
		} catch (error) {
			errors.push(error);
			setErrorMessage('Oops! Something went wrong. Please try again.');
			setIsLoading(false);
		}
	};

	return (
		<>
			<header className={css(styleSheet.header)}>
				<Button
					type='button'
					kind='custom'
					size='normal'
					className={css(styleSheet.closeButton)}
					onClick={() => onCloseModal(checkout.id)}
				>
					<XIcon />
					close
				</Button>
			</header>
			{
				<form onSubmit={handleSubmit}>
					<section className={css(bs.mb2, bs.pr1)}>
						<h3 className={css(styleSheet.paymentInfoTitle)}>Your Order</h3>
						{checkout.lineItems.map(item => (
							<div key={item.id} className={css(styleSheet.orderInfo)}>
								<p>
									Tickets <span className={css(styleSheet.timesSymbol)}>&#215;</span> <strong>{item.quantity}</strong>
								</p>
							</div>
						))}
					</section>
					<div>
						<label htmlFor='email' className={css(styleSheet.label)}>
							Email
						</label>
						<TextInput
							id='email'
							inputId='email'
							type='email'
							className={css(bs.fullWidth, bs.mb2)}
							onChange={e => setEmail(e.target.value)}
							value={email}
							placeholder='Email'
						/>
					</div>
					{isScriptLoading ? (
						<div className={css(bs.flex, bs.flexCenter)}>
							<LoadingLogo size={50} />
						</div>
					) : null}
					<PaymentElement id='payment-element' />
					<Button
						type='submit'
						disabled={isLoading}
						kind='primary'
						size='normal'
						isLoading={isLoading}
						className={css(styleSheet.submitButton)}
						loadingSpinnerSize='extra-small'
					>
						Pay {checkout.total.total.amount}
					</Button>
					{errorMessage && <small className={css(styleSheet.errorMessage)}>{errorMessage}</small>}
				</form>
			}
		</>
	);
};
